import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/index.css';
import '@/shared/lib/leaflet';
import { installMobileTables } from '@/shared/utils/mobileTables';
import { Providers } from '@/app/providers';
import { App } from '@/app/App';

// A focused number input (price/quantity fields across every sales document
// form) silently changes value on mouse-wheel scroll — a rep scrolling the
// page while their cursor happens to be over a rate field has thrown off
// totals before. Blurring it on wheel makes the page scroll normally instead.
document.addEventListener(
  'wheel',
  () => {
    const active = document.activeElement;
    if (active instanceof HTMLInputElement && active.type === 'number') active.blur();
  },
  { passive: true },
);

// The browser's saved-profile autofill can put the BDM's OWN name and email
// into customer records ("BDM 07" / rbmbdm07@gmail.com). Nothing here should be
// filled from the signed-in person's browser profile, so every field that
// doesn't ask for autofill explicitly (login and password fields do) gets it
// switched off as soon as it appears.
const AUTOFILL_UNSET = 'input:not([autocomplete]), textarea:not([autocomplete])';
const noAutofill = (root: Element | Document) => {
  if (root instanceof Element && root.matches(AUTOFILL_UNSET)) root.setAttribute('autocomplete', 'off');
  root.querySelectorAll(AUTOFILL_UNSET).forEach((el) => el.setAttribute('autocomplete', 'off'));
};
noAutofill(document);
new MutationObserver((mutations) => {
  for (const m of mutations) m.addedNodes.forEach((n) => { if (n instanceof Element) noAutofill(n); });
}).observe(document.documentElement, { childList: true, subtree: true });

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found.');

createRoot(rootEl).render(
  <StrictMode>
    <Providers>
      <App />
    </Providers>
  </StrictMode>,
);

// Tables become cards on a phone (shared/utils/mobileTables.ts).
installMobileTables();
